/*
 *   File : fallbackImage.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : <img> wrapper that renders a clean, intentional placeholder
 *                 state (neutral container + centered image icon) whenever the
 *                 source is empty or fails to load (invalid url / broken /
 *                 deleted file). State-driven so it survives re-renders and can
 *                 never flash the browser's broken-image icon. Shared by the
 *                 upload preview and the WhatsApp/webinar preview.
 *   Version : v2.0
 */

import React, { useState, useEffect } from 'react';

// Generic "image" icon (frame + sun + mountain), matching the WhatsApp template
// preview placeholder style. Inline so it can never 404.
const PlaceholderIcon = () => (
    <svg
        width="46"
        height="46"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#9aa0a6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </svg>
);

const FallbackImage = ({ src, alt = "", style, placeholderStyle, ...rest }) => {
    const [errored, setErrored] = useState(false);

    // Reset the error state whenever the source changes so a newly uploaded /
    // valid image gets a fresh chance to load.
    useEffect(() => {
        setErrored(false);
    }, [src]);

    const showPlaceholder = !src || errored;

    if (showPlaceholder) {
        return (
            <div
                className="e-img-placeholder"
                style={{
                    width: '100%',
                    minHeight: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f0f0f0',
                    border: '1px solid #e3e3e3',
                    borderRadius: '8px',
                    ...placeholderStyle,
                }}
                aria-label="No image available"
            >
                <PlaceholderIcon />
            </div>
        );
    }

    return (
        <img
            {...rest}
            src={src}
            alt={alt}
            style={style}
            onError={() => {
                // Flip to the placeholder state once; the placeholder is plain
                // markup (no <img>), so there is no further onError to loop on.
                if (!errored) setErrored(true);
            }}
        />
    );
};

export default FallbackImage;
